import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { MenuIcon, BellIcon, UserIcon, LogOutIcon, ChevronDownIcon, SettingsIcon, HelpCircleIcon } from 'lucide-react';
import Badge from '../ui/Badge';
interface HeaderProps {
  sidebarOpen: boolean;
  setSidebarOpen: (open: boolean) => void;
}
const Header: React.FC<HeaderProps> = ({
  sidebarOpen,
  setSidebarOpen
}) => {
  const {
    user,
    logout
  } = useAuth();
  const navigate = useNavigate();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const handleLogout = () => {
    logout();
    navigate('/login');
  };
  const toggleUserMenu = () => {
    setShowUserMenu(!showUserMenu);
    if (showNotifications) setShowNotifications(false);
  };
  const toggleNotifications = () => {
    setShowNotifications(!showNotifications);
    if (showUserMenu) setShowUserMenu(false);
  };
  return <header className="bg-white shadow-sm z-10 border-b border-gray-200" data-id="element-23">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-24">
        <div className="flex justify-between h-16" data-id="element-25">
          <div className="flex" data-id="element-26">
            <button type="button" className="px-4 text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500 md:hidden" onClick={() => setSidebarOpen(!sidebarOpen)} data-id="element-27">
              <span className="sr-only" data-id="element-28">Open sidebar</span>
              <MenuIcon className="h-6 w-6" aria-hidden="true" data-id="element-29" />
            </button>
            <div className="flex-shrink-0 flex items-center" data-id="element-30">
              <h1 className="text-xl font-bold bg-gradient-to-r from-blue-600 to-blue-800 bg-clip-text text-transparent" data-id="element-31">
                Enterprise Portal
              </h1>
            </div>
          </div>
          <div className="flex items-center space-x-4" data-id="element-32">
            {/* Notifications */}
            <div className="relative" data-id="element-33">
              <button type="button" className="p-1 rounded-full text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 relative" onClick={toggleNotifications} data-id="element-34">
                <span className="sr-only" data-id="element-35">View notifications</span>
                <BellIcon className="h-6 w-6" aria-hidden="true" data-id="element-36" />
                <span className="absolute top-0 right-0 block h-2 w-2 rounded-full bg-red-500 ring-2 ring-white" data-id="element-37"></span>
              </button>
              {/* Notification dropdown */}
              {showNotifications && <div className="origin-top-right absolute right-0 mt-2 w-80 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none z-50" data-id="element-38">
                  <div className="py-2 px-3 border-b border-gray-200" data-id="element-39">
                    <h3 className="text-sm font-semibold text-gray-800" data-id="element-40">
                      Notifications
                    </h3>
                  </div>
                  <div className="max-h-96 overflow-y-auto" data-id="element-41">
                    {[1, 2, 3].map(item => <div key={item} className="px-4 py-3 hover:bg-gray-50 border-b border-gray-100" data-id="element-42">
                        <div className="flex items-start" data-id="element-43">
                          <div className="flex-shrink-0 pt-0.5" data-id="element-44">
                            <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center" data-id="element-45">
                              <BellIcon className="h-4 w-4 text-blue-600" data-id="element-46" />
                            </div>
                          </div>
                          <div className="ml-3 w-0 flex-1" data-id="element-47">
                            <p className="text-sm font-medium text-gray-900" data-id="element-48">
                              System Notification
                            </p>
                            <p className="text-xs text-gray-500" data-id="element-49">
                              New update available
                            </p>
                            <p className="mt-1 text-xs text-gray-400" data-id="element-50">
                              2 hours ago
                            </p>
                          </div>
                        </div>
                      </div>)}
                  </div>
                  <div className="py-2 px-3 border-t border-gray-200 bg-gray-50" data-id="element-51">
                    <a href="#" className="text-xs font-medium text-blue-600 hover:text-blue-500" data-id="element-52">
                      View all notifications
                    </a>
                  </div>
                </div>}
            </div>
            {/* Profile dropdown */}
            <div className="relative" data-id="element-53">
              <div data-id="element-54">
                <button type="button" className="flex items-center max-w-xs text-sm rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500" onClick={toggleUserMenu} data-id="element-55">
                  <div className="flex items-center" data-id="element-56">
                    <div className="flex-shrink-0" data-id="element-57">
                      <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center" data-id="element-58">
                        <UserIcon className="h-5 w-5 text-blue-600" data-id="element-59" />
                      </div>
                    </div>
                    <div className="ml-3 hidden md:block" data-id="element-60">
                      <div className="text-sm font-medium text-gray-700" data-id="element-61">
                        {user?.username}
                      </div>
                      <div className="text-xs text-gray-500 capitalize flex items-center" data-id="element-62">
                        <Badge variant="primary" size="sm" dot={true} data-id="element-63">
                          {user?.role} Role
                        </Badge>
                      </div>
                    </div>
                    <ChevronDownIcon className="h-4 w-4 ml-1 text-gray-400" data-id="element-64" />
                  </div>
                </button>
              </div>
              {/* User menu dropdown */}
              {showUserMenu && <div className="origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none z-50" data-id="element-65">
                  <div className="py-1" data-id="element-66">
                    <a href="#" className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100" data-id="element-67">
                      <UserIcon className="mr-3 h-4 w-4 text-gray-500" data-id="element-68" />
                      Profile
                    </a>
                    <a href="#" className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100" data-id="element-69">
                      <SettingsIcon className="mr-3 h-4 w-4 text-gray-500" data-id="element-70" />
                      Settings
                    </a>
                    <a href="#" className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100" data-id="element-71">
                      <HelpCircleIcon className="mr-3 h-4 w-4 text-gray-500" data-id="element-72" />
                      Help
                    </a>
                    <div className="border-t border-gray-100" data-id="element-73"></div>
                    <button onClick={handleLogout} className="flex w-full items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100" data-id="element-74">
                      <LogOutIcon className="mr-3 h-4 w-4 text-gray-500" data-id="element-75" />
                      Sign out
                    </button>
                  </div>
                </div>}
            </div>
          </div>
        </div>
      </div>
    </header>;
};
export default Header;